import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import {
  getIdentityExpiryStatus,
  getIdentityExpiryStatusLabel,
  isIdentityDocumentCurrentlyValid,
} from '../../services/identityDocumentService';

// ─── Visibility checker (mirrors photographerService._fetchAllPhotographers) ──
function getVisibilityReasons(p) {
  const reasons = [];
  if (!p.available_for_booking)           reasons.push({ key: 'booking',     label: 'غير متاح للحجز',          fix: 'تفعيل "متاح للحجز" من ملف المصور' });
  if (!p.identity_verified)               reasons.push({ key: 'identity',    label: 'الهوية غير موثّقة',         fix: 'التحقق من الهوية في قسم التحقق' });
  else if (!isIdentityDocumentCurrentlyValid(p)) {
    const expiryLabel = p.identity_document_expires_at
      ? getIdentityExpiryStatusLabel(getIdentityExpiryStatus(p.identity_document_expires_at))
      : 'منتهية';
    reasons.push({ key: 'identity_expired', label: `مستند الهوية ${expiryLabel}`, fix: 'على المصور رفع هوية سارية وإعادة التوثيق' });
  }
  if (p.verification_status !== 'approved') reasons.push({ key: 'verified',  label: `حالة التحقق: ${p.verification_status || 'غير موافق'}`, fix: 'الموافقة على طلب التحقق' });
  if (p.status && p.status !== 'approved')  reasons.push({ key: 'status',    label: `حالة الحساب: ${p.status}`, fix: 'تفعيل الحساب من قسم المصورين' });
  if ((p.profile_completion_pct ?? 0) < 100) reasons.push({ key: 'profile', label: `اكتمال الملف: ${p.profile_completion_pct ?? 0}%`, fix: 'على المصور إكمال ملفه الشخصي' });
  const now = new Date();
  if (p.visibility_penalty_until && new Date(p.visibility_penalty_until) > now)
    reasons.push({ key: 'withdrawal_visibility', label: `مخفي بسبب الانسحاب حتى ${fmtDate(p.visibility_penalty_until)}`, fix: 'رفع عقوبة الإخفاء', canLift: 'visibility' });
  if (p.withdrawal_suspended_until && new Date(p.withdrawal_suspended_until) > now)
    reasons.push({ key: 'withdrawal_suspended', label: `معلق بسبب الانسحاب حتى ${fmtDate(p.withdrawal_suspended_until)}`, fix: 'رفع التعليق', canLift: 'suspension' });
  // ملاحظة: hasPaidConfirmedToday لا يُحسب ضمن أسباب الإخفاء الدائمة — هو حالة مؤقتة تنتهي بنهاية اليوم
  return reasons;
}

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDateShort = (iso) => iso ? new Date(iso).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'short' }) : '—';

const PENALTY_LABELS = { warning: 'تحذير فقط', visibility: 'إخفاء 7 أيام', suspension: 'تعليق 14 يوم' };

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminPhotographerVisibility() {
  const navigate = useNavigate();
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | visible | hidden
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null); // user_id of expanded card
  const [notice, setNotice] = useState(null);
  const [lifting, setLifting] = useState(null); // user_id being updated

  const showNotice = (msg, type = 'success') => {
    setNotice({ msg, type });
    setTimeout(() => setNotice(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: profiles, error } = await supabase
        .from('photographer_profiles')
        .select(`
          user_id, business_name, status, verification_status,
          identity_verified, identity_document_expires_at, phone_verified, available_for_booking,
          profile_completion_pct, city, experience_level,
          withdrawal_count, withdrawal_suspended_until, visibility_penalty_until,
          created_at
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!profiles?.length) { setPhotographers([]); return; }

      const ids = profiles.map(p => p.user_id);
      const { data: userProfiles } = await supabase
        .from('user_profiles')
        .select('id, full_name, phone, avatar_url')
        .in('id', ids);

      const userMap = Object.fromEntries((userProfiles || []).map(u => [u.id, u]));

      // Fetch withdrawal history per photographer
      const { data: withdrawals } = await supabase
        .from('bookings')
        .select('assigned_photographer_id, id, booking_date, cancellation_requested_at, cancellation_reason')
        .eq('status', 'cancelled_by_photographer')
        .in('assigned_photographer_id', ids)
        .order('cancellation_requested_at', { ascending: false });

      const withdrawalMap = {};
      (withdrawals || []).forEach(w => {
        if (!withdrawalMap[w.assigned_photographer_id]) withdrawalMap[w.assigned_photographer_id] = [];
        withdrawalMap[w.assigned_photographer_id].push(w);
      });

      // جلب المصورين الذين لديهم حجز مؤكد ومدفوع اليوم
      const todayRiyadh = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Riyadh' });
      const { data: busyToday } = await supabase
        .from('bookings')
        .select('photographer_id, assigned_photographer_id')
        .eq('status', 'paid_confirmed')
        .eq('booking_date', todayRiyadh);

      const busyTodayIds = new Set();
      (busyToday || []).forEach(b => {
        if (b.photographer_id) busyTodayIds.add(b.photographer_id);
        if (b.assigned_photographer_id) busyTodayIds.add(b.assigned_photographer_id);
      });

      setPhotographers(profiles.map(p => {
        const enriched = { ...p, hasPaidConfirmedToday: busyTodayIds.has(p.user_id) };
        return {
          ...enriched,
          user: userMap[p.user_id] || {},
          withdrawalHistory: withdrawalMap[p.user_id] || [],
          visibilityReasons: getVisibilityReasons(enriched),
        };
      }));
    } catch (e) {
      console.error(e);
      showNotice('فشل تحميل البيانات', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const liftPenalty = async (photographer, type) => {
    setLifting(photographer.user_id);
    try {
      const patch = type === 'visibility'
        ? { visibility_penalty_until: null }
        : { withdrawal_suspended_until: null };
      const { error } = await supabase
        .from('photographer_profiles')
        .update(patch)
        .eq('user_id', photographer.user_id);
      if (error) throw error;
      showNotice(`✅ تم رفع ${type === 'visibility' ? 'عقوبة الإخفاء' : 'التعليق'} عن ${photographer.business_name || photographer.user?.full_name}`);
      load();
    } catch (e) {
      showNotice(e.message || 'فشل العملية', 'error');
    } finally {
      setLifting(null);
    }
  };

  const resetWithdrawalCount = async (photographer) => {
    if (!window.confirm('إعادة تصفير عداد الانسحابات؟ ستُحذف جميع العقوبات المرتبطة.')) return;
    setLifting(photographer.user_id);
    try {
      await supabase.from('photographer_profiles').update({
        withdrawal_count: 0,
        withdrawal_suspended_until: null,
        visibility_penalty_until: null,
      }).eq('user_id', photographer.user_id);
      showNotice(`✅ تم إعادة تصفير سجل الانسحابات`);
      load();
    } catch (e) {
      showNotice(e.message, 'error');
    } finally {
      setLifting(null);
    }
  };

  const filtered = photographers.filter(p => {
    const isVisible = p.visibilityReasons.length === 0;
    if (filter === 'visible' && !isVisible) return false;
    if (filter === 'hidden' && isVisible) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
            p.business_name?.toLowerCase().includes(s) ||
        p.user?.full_name?.toLowerCase().includes(s) ||
        p.user?.phone?.includes(s) ||
        p.city?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const visibleCount = photographers.filter(p => p.visibilityReasons.length === 0).length;
  const hiddenCount = photographers.length - visibleCount;
  const withdrawalCount = photographers.filter(p => (p.withdrawal_count || 0) > 0).length;

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8" dir="rtl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/admin')} className="p-2 rounded-xl hover:bg-slate-200 transition-colors">
          <Icon name="ArrowRight" size={20} className="text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">ظهور المصورين</h1>
          <p className="text-sm text-slate-500">مراقبة وإدارة ظهور المصورين في صفحة الاستكشاف</p>
        </div>
        <button onClick={load} className="mr-auto p-2 rounded-xl hover:bg-slate-200 transition-colors" title="تحديث">
          <Icon name="RefreshCw" size={18} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Notice */}
      {notice && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${notice.type === 'success' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-red-100 text-red-800 border border-red-300'}`}>
          <Icon name={notice.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {notice.msg}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard color="blue" icon="Users" label="إجمالي المصورين" value={photographers.length} />
        <StatCard color="green" icon="Eye" label="يظهرون في الاستكشاف" value={visibleCount} />
        <StatCard color="red" icon="EyeOff" label="مخفيون" value={hiddenCount} />
        <StatCard color="orange" icon="AlertTriangle" label="لديهم انسحابات" value={withdrawalCount} />
      </div>

      {/* Filters + Search */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm mb-4">
        <div className="flex items-center gap-3 p-4 border-b border-slate-100 flex-wrap">
          <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
            {[
              { id: 'all', label: 'الكل', count: photographers.length },
              { id: 'visible', label: '✅ يظهرون', count: visibleCount },
              { id: 'hidden', label: '🚫 مخفيون', count: hiddenCount },
            ].map(t => (
              <button key={t.id} onClick={() => setFilter(t.id)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${filter === t.id ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                {t.label}
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${filter === t.id ? 'bg-slate-100 text-slate-700' : 'bg-white text-slate-500'}`}>{t.count}</span>
              </button>
            ))}
          </div>
          <div className="relative mr-auto">
            <Icon name="Search" size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="بحث بالاسم أو الجوال أو المدينة..."
              className="pr-9 pl-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 w-64" />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="p-16 text-center text-slate-400">
            <Icon name="Loader" size={36} className="mx-auto mb-3 animate-spin" />
            <p>جاري تحميل البيانات...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <Icon name="Users" size={48} className="mx-auto mb-3 opacity-30" />
            <p className="font-medium">لا توجد نتائج</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(p => (
              <PhotographerRow
                key={p.user_id}
                p={p}
                expanded={expanded === p.user_id}
                onToggle={() => setExpanded(expanded === p.user_id ? null : p.user_id)}
                onLift={liftPenalty}
                onReset={resetWithdrawalCount}
                lifting={lifting === p.user_id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Photographer Row ─────────────────────────────────────────────────────────
function PhotographerRow({ p, expanded, onToggle, onLift, onReset, lifting }) {
  const isVisible = p.visibilityReasons.length === 0;
  const count = p.withdrawal_count || 0;
  const now = new Date();
  const hasSuspension = p.withdrawal_suspended_until && new Date(p.withdrawal_suspended_until) > now;
  const hasVisibilityPenalty = p.visibility_penalty_until && new Date(p.visibility_penalty_until) > now;

  return (
    <div className={`transition-colors ${expanded ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`}>
      {/* Main row */}
      <button className="w-full text-right px-5 py-4" onClick={onToggle}>
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <div className="w-11 h-11 rounded-full bg-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
            {p.user?.avatar_url
              ? <img src={p.user.avatar_url} alt="" className="w-full h-full object-cover" />
              : <Icon name="User" size={20} className="text-slate-400" />}
          </div>

          {/* Name + city */}
          <div className="flex-1 min-w-0 text-right">
            <p className="font-bold text-slate-900 truncate">{p.business_name || p.user?.full_name || '—'}</p>
            <p className="text-xs text-slate-400">{p.city || '—'} · {p.user?.phone || '—'}</p>
          </div>

          {/* Visibility badge */}
          <div className="shrink-0">
            {isVisible ? (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-green-100 text-green-700">
                <Icon name="Eye" size={12} /> يظهر
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-red-100 text-red-700">
                <Icon name="EyeOff" size={12} /> مخفي · {p.visibilityReasons.length} سبب
              </span>
            )}
          </div>

          {/* Withdrawal badge */}
          {count > 0 && (
            <div className={`shrink-0 text-xs font-bold px-2 py-1 rounded-full ${hasSuspension ? 'bg-red-100 text-red-700' : hasVisibilityPenalty ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-600'}`}>
              {count} انسحاب {hasSuspension ? '⛔' : hasVisibilityPenalty ? '👁' : ''}
            </div>
          )}

          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-slate-400 shrink-0" />
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-5 pb-5 space-y-4">

          {/* Visibility reasons */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200">
              <p className="text-sm font-bold text-slate-700">شروط الظهور في الاستكشاف</p>
            </div>
            <div className="divide-y divide-slate-100">
              {[
                { key: 'available_for_booking', label: 'متاح للحجز', ok: !!p.available_for_booking },
                { key: 'identity_verified', label: 'الهوية موثّقة', ok: !!p.identity_verified },
                { key: 'verification_status', label: `حالة التحقق (${p.verification_status || '—'})`, ok: p.verification_status === 'approved' },
                { key: 'account_status', label: `حالة الحساب (${p.status || 'approved'})`, ok: !p.status || p.status === 'approved' },
                { key: 'profile_completion', label: `اكتمال الملف الشخصي (${p.profile_completion_pct ?? 0}%)`, ok: (p.profile_completion_pct ?? 0) >= 100 },
                { key: 'visibility_penalty', label: `لا توجد عقوبة إخفاء${hasVisibilityPenalty ? ` (حتى ${fmtDate(p.visibility_penalty_until)})` : ''}`, ok: !hasVisibilityPenalty },
                { key: 'busy_today', label: p.hasPaidConfirmedToday ? 'يوجد حجز مؤكد ومدفوع اليوم — مخفي اليوم فقط' : 'لا يوجد حجز مؤكد ومدفوع اليوم', ok: !p.hasPaidConfirmedToday },
              ].map(c => (
                <div key={c.key} className="flex items-center gap-3 px-4 py-2.5">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${c.ok ? 'bg-green-100' : 'bg-red-100'}`}>
                    <Icon name={c.ok ? 'Check' : 'X'} size={11} className={c.ok ? 'text-green-600' : 'text-red-600'} />
                  </span>
                  <span className={`text-sm flex-1 ${c.ok ? 'text-slate-700' : 'text-red-700 font-medium'}`}>{c.label}</span>
                  {!c.ok && c.key === 'visibility_penalty' && hasVisibilityPenalty && (
                    <button onClick={() => onLift(p, 'visibility')} disabled={lifting}
                      className="text-xs font-bold text-orange-700 bg-orange-50 border border-orange-200 hover:bg-orange-100 rounded-lg px-3 py-1 transition disabled:opacity-50">
                      {lifting ? '...' : 'رفع الإخفاء'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Withdrawal history */}
          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
              <p className="text-sm font-bold text-slate-700">
                سجل الانسحابات
                {count > 0 && (
                  <span className={`mr-2 text-xs font-bold px-2 py-0.5 rounded-full ${hasSuspension ? 'bg-red-100 text-red-700' : count >= 2 ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>
                    {count} انسحاب
                  </span>
                )}
              </p>
              <div className="flex gap-2">
                {hasSuspension && (
                  <button onClick={() => onLift(p, 'suspension')} disabled={lifting}
                    className="text-xs font-bold text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-lg px-3 py-1 transition disabled:opacity-50">
                    {lifting ? '...' : 'رفع التعليق'}
                  </button>
                )}
                {count > 0 && (
                  <button onClick={() => onReset(p)} disabled={lifting}
                    className="text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-1 transition disabled:opacity-50">
                    إعادة تصفير العداد
                  </button>
                )}
              </div>
            </div>

            {count === 0 ? (
              <div className="px-4 py-4 text-sm text-slate-400 text-center">لا توجد انسحابات</div>
            ) : (
              <div>
                {/* Current penalty status */}
                <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-100 grid grid-cols-3 gap-4 text-center text-xs">
                  <div>
                    <p className="text-slate-400 mb-1">عدد الانسحابات</p>
                    <p className={`text-lg font-black ${count >= 3 ? 'text-red-600' : count === 2 ? 'text-orange-600' : 'text-amber-600'}`}>{count}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">العقوبة المطبّقة</p>
                    <p className={`font-bold ${hasSuspension ? 'text-red-600' : hasVisibilityPenalty ? 'text-orange-600' : count === 1 ? 'text-amber-600' : 'text-green-600'}`}>
                      {hasSuspension ? 'تعليق' : hasVisibilityPenalty ? 'إخفاء' : count === 1 ? 'تحذير' : 'لا عقوبة'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 mb-1">تنتهي في</p>
                    <p className="font-bold text-slate-700">
                      {hasSuspension ? fmtDateShort(p.withdrawal_suspended_until)
                       : hasVisibilityPenalty ? fmtDateShort(p.visibility_penalty_until)
                       : '—'}
                    </p>
                  </div>
                </div>

                {/* History list */}
                {p.withdrawalHistory.length > 0 ? (
                  <div className="divide-y divide-slate-100 max-h-52 overflow-y-auto">
                    {p.withdrawalHistory.map((w, i) => (
                      <div key={w.id} className="flex items-start gap-3 px-4 py-2.5">
                        <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 text-[10px] font-black flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-500">{w.cancellation_requested_at ? fmtDate(w.cancellation_requested_at) : '—'}</p>
                          {w.cancellation_reason && <p className="text-xs text-slate-700 truncate">"{w.cancellation_reason}"</p>}
                        </div>
                        <span className="text-[10px] text-slate-400 shrink-0">{w.booking_date ? new Date(w.booking_date).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn') : '—'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-xs text-slate-400">لا يوجد تفاصيل إضافية</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────
const colorMap = {
  blue:   'bg-blue-50 border-blue-200 text-blue-600 text-blue-800',
  green:  'bg-green-50 border-green-200 text-green-600 text-green-800',
  red:    'bg-red-50 border-red-200 text-red-600 text-red-800',
  orange: 'bg-orange-50 border-orange-200 text-orange-600 text-orange-800',
};
function StatCard({ color, icon, label, value }) {
  const [bg, border, ic, val] = colorMap[color].split(' ');
  return (
    <div className={`${bg} border ${border} rounded-2xl p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name={icon} size={15} className={ic} />
        <p className="text-xs text-slate-500 font-medium">{label}</p>
      </div>
      <p className={`text-2xl font-black ${val}`}>{value}</p>
    </div>
  );
}
